'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, getApiErrorMessage } from '@/lib/api';
import type { Dock } from '@/types/dock';
import { hasCapability } from '@/lib/dock-status';
import type { ProjectionAircraft } from '@/lib/coverageProjection';
import { DEFAULT_SWAP_DURATION_SECONDS, resolveSwapDurationSeconds } from '@/lib/dockSwap';

/**
 * THE FACTS THE BROWSER CANNOT COMPUTE, for the attach wizard's preview (PR 3, S5).
 *
 * The projection in `@/lib/coverageProjection` is pure and takes everything as arguments. Three of
 * those arguments are not knowable in a browser: an airframe's usable endurance, its calibrated
 * cruise speed, and where its dock is and what kind of machine it is. This hook fetches them.
 *
 * WHY THEY ARE FETCHED RATHER THAN ASSUMED. `src/lib/airframe-endurance.ts` states the rule the
 * whole feature obeys: a substituted default is indistinguishable from a measurement, and #177 is
 * what breaking it looked like in production. A nominal-endurance table in the browser would put
 * exactly such a substitution under the figure an operator uses to decide how many aircraft a
 * window needs. So an aircraft this platform cannot size arrives here as `null` and the projection
 * refuses it by name.
 *
 * THREE READS, NOT ONE, and deliberately: `GET /api/drones/energy` is Fleet's answer about
 * airframes, `GET /api/docks` is the Dock module's about machines, and `GET /api/org` carries the
 * org's configured battery-swap duration. Collapsing the first two would have meant a fleet route
 * reaching across into the dock module for coordinates.
 *
 * THE SWAP DURATION IS IN HERE, and not in a hook of its own, for the rule stated below. It is a
 * first-order term in a swap dock's turnaround, so it feeds `coveredPct` and the MEETS/UNDER
 * verdict directly - it is exactly the class of input this hook exists to refuse to guess at. A
 * standalone hook that fell back to the platform default on a failed read would substitute 300 s
 * under an org that had configured 900, which produces MORE apparent coverage than the platform
 * will deliver: the fail-open direction, in the surface whose whole purpose is answering "how many
 * aircraft do I need". It rides the same `status`, so a failed read suppresses the preview and
 * offers a Retry rather than quietly drawing an optimistic one.
 *
 * A READ THAT FAILED IS NOT A FLEET WITH NO ENDURANCE. The two produce the same picture - every
 * aircraft refused - and completely different remedies: one is a retry, the other is a calibration
 * flight. `status` keeps them apart, exactly as `dronesStatus` does for the fleet list beside it.
 */

/** One aircraft's energy resolutions, as `GET /api/drones/energy` returns them. */
interface EnergyRow {
  drone_id: string;
  callsign: string | null;
  name: string;
  model: string | null;
  endurance:
    | { outcome: 'resolved'; minutesAtFull: number; source: string }
    | { outcome: 'unknown'; reason: string; message: string };
  cruise:
    | { outcome: 'resolved'; cruiseSpeedMs: number; source: string }
    | { outcome: 'unknown'; reason: string; message: string };
}

/** What the platform could resolve about one airframe. `null` is always "unknown", never a zero. */
export interface AircraftEnergy {
  readonly enduranceMinutesAtFull: number | null;
  /**
   * Where the endurance came from: `sampled` is the only measurement, `config_override` is a
   * human's typed figure and `config_model_default` is 75% of the model's nominal. Three very
   * different claims that all read as "38 minutes" without it.
   */
  readonly enduranceSource: string | null;
  readonly cruiseSpeedMs: number | null;
  /**
   * Where the cruise speed came from, and it is NOT a detail.
   *
   * `resolveCruiseSpeedMs` has two answers: `profile` (observed for this airframe) and
   * `mission_default` - `missions.default_speed`, a column carrying `DEFAULT 5` in the schema. The
   * on-station figure is endurance minus BOTH transits, and a transit is distance over this speed,
   * so the cruise is a first-order term in it. Dropping this source made it possible to show
   * "9m 36s on station" as measured when the dominant term was a 5 m/s default nobody flew, and to
   * swing the same aircraft to 20m 48s by editing the mission's planned speed.
   */
  readonly cruiseSource: string | null;
}

export interface PlanInputs {
  /** Keyed by drone id. An aircraft absent from this map has no resolutions at all. */
  readonly energyByDrone: ReadonlyMap<string, AircraftEnergy>;
  /** Keyed by dock id, so the caller can resolve a drone's own `dock_id`. */
  readonly dockById: ReadonlyMap<string, Dock>;
  readonly status: 'loading' | 'ready' | 'error';
  readonly error: string | null;
  /**
   * The org's `settings.dock.swap_duration_s`, seconds.
   *
   * Meaningful only while `status` is `ready`; on `loading` and `error` it carries the platform
   * default so the type stays a plain number, and the caller must not draw with it - the surfaces
   * that consume this hook already gate on `status` for exactly that reason.
   */
  readonly swapDurationSeconds: number;
  readonly reload: () => void;
}

const NO_ENERGY: ReadonlyMap<string, AircraftEnergy> = new Map();
const NO_DOCKS: ReadonlyMap<string, Dock> = new Map();

export function useCoveragePlanInputs(missionId: string, enabled: boolean): PlanInputs {
  const [state, setState] = useState<{
    energyByDrone: ReadonlyMap<string, AircraftEnergy>;
    dockById: ReadonlyMap<string, Dock>;
    swapDurationSeconds: number;
    status: PlanInputs['status'];
    error: string | null;
  }>({
    energyByDrone: NO_ENERGY,
    dockById: NO_DOCKS,
    swapDurationSeconds: DEFAULT_SWAP_DURATION_SECONDS,
    status: 'loading',
    error: null,
  });
  const [revision, setRevision] = useState(0);

  /** So a slow read for one mission can never paint another mission's numbers. */
  const current = useRef(missionId);

  const reload = useCallback(() => setRevision((r) => r + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    current.current = missionId;
    let cancelled = false;

    void (async () => {
      try {
        const [energy, docks, org] = await Promise.all([
          api.get<{ data: EnergyRow[] }>(
            `/api/drones/energy?mission_id=${encodeURIComponent(missionId)}`,
          ),
          api.get<{ data: Dock[] }>('/api/docks'),
          api.get<{ data: { settings?: unknown } }>('/api/org'),
        ]);
        if (cancelled || current.current !== missionId) return;

        const energyByDrone = new Map<string, AircraftEnergy>();
        for (const row of energy.data ?? []) {
          energyByDrone.set(row.drone_id, {
            enduranceMinutesAtFull:
              row.endurance?.outcome === 'resolved' ? row.endurance.minutesAtFull : null,
            enduranceSource: row.endurance?.outcome === 'resolved' ? row.endurance.source : null,
            cruiseSpeedMs: row.cruise?.outcome === 'resolved' ? row.cruise.cruiseSpeedMs : null,
            cruiseSource: row.cruise?.outcome === 'resolved' ? row.cruise.source : null,
          });
        }
        setState({
          energyByDrone,
          dockById: new Map((docks.data ?? []).map((d) => [d.id, d])),
          // Total over a missing or malformed `settings` - an org that never opened Settings has
          // no `dock` key at all, and that is the platform default, not a fault.
          swapDurationSeconds: resolveSwapDurationSeconds(org.data?.settings),
          status: 'ready',
          error: null,
        });
      } catch (err) {
        if (cancelled || current.current !== missionId) return;
        setState({
          energyByDrone: NO_ENERGY,
          dockById: NO_DOCKS,
          swapDurationSeconds: DEFAULT_SWAP_DURATION_SECONDS,
          status: 'error',
          error: getApiErrorMessage(err, 'The planning inputs could not be read'),
        });
      }
    })();

    return () => { cancelled = true; };
  }, [missionId, enabled, revision]);

  return { ...state, reload };
}

/** A dock as the projection needs it: where it is, and whether it swaps packs or charges them. */
export function projectionDock(dock: Dock | undefined): ProjectionAircraft['dock'] {
  if (!dock) return null;
  return {
    name: dock.name,
    lat: dock.latitude,
    lon: dock.longitude,
    // The one definition of a swap dock in this codebase, read through the shared helper the
    // dock sidebar already uses rather than re-implemented here.
    isSwap: hasCapability(dock.capabilities, 'battery_switch'),
  };
}
